<template>
  <div class="p-pagination">
    <div class="p-pagination__item">
      <p-icon icon="caret-left" size="xs" @click="handlePrev" />
    </div>
    <nuxt-link
      :class="[
        'p-pagination__item f-utility-14',
        { 'p-pagination__item--active': 1 === modelValue },
      ]"
      :to="{ query: { ...$route.query, page: 1 } }"
    >
      1
    </nuxt-link>
    <nuxt-link v-if="5 < modelValue" class="p-pagination__item f-utility-14"> ... </nuxt-link>
    <template v-for="page in maxPageNumber" :key="page">
      <nuxt-link
        v-if="shouldShowPage(page)"
        :class="[
          'p-pagination__item f-utility-14',
          { 'p-pagination__item--active': page === modelValue },
        ]"
        :to="{ query: { ...$route.query, page } }"
      >
        {{ page }}
      </nuxt-link>
    </template>
    <nuxt-link v-if="modelValue + 4 < maxPageNumber" class="p-pagination__item f-utility-14">
      ...
    </nuxt-link>
    <nuxt-link
      v-if="1 != maxPageNumber"
      :class="[
        'p-pagination__item f-utility-14',
        { 'p-pagination__item--active': maxPageNumber === modelValue },
      ]"
      :to="{ query: { ...$route.query, page: maxPageNumber } }"
    >
      {{ maxPageNumber }}
    </nuxt-link>
    <div class="p-pagination__item">
      <p-icon icon="caret-right" size="xs" @click="handleNext" />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps({
  maxPageNumber: {
    type: Number,
    required: true,
  },
})

const emit = defineEmits(['on-next', 'on-prev'])

const $router = useRouter()
const $route = useRoute()

const modelValue = defineModel<number>('modelValue', { default: 1 })

const shouldShowPage = (page: number): boolean => {
  const start =
    modelValue.value > 5
      ? props.maxPageNumber - 4 < modelValue.value
        ? props.maxPageNumber - 6
        : modelValue.value - 2
      : 2
  const end =
    start + 6 < props.maxPageNumber
      ? modelValue.value > 5
        ? start + 4
        : start + 5
      : props.maxPageNumber - 1
  return start <= page && page <= end
}

onMounted(() => {
  const queryPage = Number($route.query.page) || modelValue.value

  if (queryPage !== modelValue.value) {
    updatePage(queryPage)
  }
})

watch(
  () => $route.query.page,
  (newPage) => {
    const pageNumber = Number(newPage) || 1
    if (pageNumber < 1 || pageNumber > props.maxPageNumber) {
      updatePage(1)
    } else {
      modelValue.value = pageNumber
    }
  },
  { immediate: true },
)

function handlePrev(): void {
  emit('on-prev')
  if (modelValue.value > 1) {
    updatePage(modelValue.value - 1)
  }
}

function handleNext(): void {
  emit('on-next')
  if (modelValue.value < props.maxPageNumber) {
    updatePage(modelValue.value + 1)
  }
}

function updatePage(newPage: number): void {
  modelValue.value = newPage
  $router.replace({ query: { ...$route.query, page: newPage } })
}
</script>
